import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  FilterDrawerFieldConfig,
  FilterDrawerInitialValue,
  FilterDrawerOption,
  FilterDrawerOptionValue,
  FilterDrawerValue,
} from './filter-drawer.models';

@Component({
  selector: 'app-filter-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './filter-drawer.component.html',
  styleUrl: './filter-drawer.component.scss',
})
export class FilterDrawerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() isOpen = false;
  @Input() title = 'Filters';
  @Input() subtitle = 'Adjust the fields below to refine the result set.';
  @Input() submitLabel = 'Apply Filters';
  @Input() resetLabel = 'Clear all';
  @Input() fields: FilterDrawerFieldConfig[] = [];
  @Input() initialValue: FilterDrawerInitialValue = {};

  @Output() close = new EventEmitter<void>();
  @Output() apply = new EventEmitter<FilterDrawerValue>();

  form = new FormGroup<Record<string, FormControl<unknown>>>({});

  searchableTerms: Record<string, string> = {};
  openSearchableFieldKey: string | null = null;
  private readonly hostElement: HTMLElement;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.hostElement = this.elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.renderer.appendChild(this.document.body, this.hostElement);
  }

  ngOnDestroy(): void {
    if (this.hostElement.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, this.hostElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['initialValue']) {
      this.rebuildForm();
    }

    if (!this.isOpen) {
      this.openSearchableFieldKey = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  trackByField(index: number, field: FilterDrawerFieldConfig): string {
    return `${field.key}-${index}`;
  }

  onClose(): void {
    this.openSearchableFieldKey = null;
    this.close.emit();
  }

  onReset(): void {
    this.rebuildForm(true);
    this.openSearchableFieldKey = null;
    this.apply.emit(this.getSanitizedValue());
  }

  onSubmit(): void {
    this.apply.emit(this.getSanitizedValue());
    this.openSearchableFieldKey = null;
  }

  getControl(fieldKey: string): FormControl<unknown> {
    return this.form.controls[fieldKey];
  }

  getCheckboxGroupValue(fieldKey: string): FilterDrawerOptionValue[] {
    const value = this.getControl(fieldKey)?.value;
    return Array.isArray(value) ? value : [];
  }

  isCheckboxGroupChecked(
    fieldKey: string,
    optionValue: FilterDrawerOption['value'],
  ): boolean {
    return this.getCheckboxGroupValue(fieldKey).includes(optionValue);
  }

  onCheckboxGroupChange(
    fieldKey: string,
    optionValue: FilterDrawerOption['value'],
    checked: boolean,
  ): void {
    const nextValue = new Set(this.getCheckboxGroupValue(fieldKey));

    if (checked) {
      nextValue.add(optionValue);
    } else {
      nextValue.delete(optionValue);
    }

    this.getControl(fieldKey).setValue(Array.from(nextValue));
  }

  toggleSearchableSelect(fieldKey: string): void {
    this.openSearchableFieldKey =
      this.openSearchableFieldKey === fieldKey ? null : fieldKey;
  }

  getSelectedSearchableOptionLabel(field: FilterDrawerFieldConfig): string {
    const selectedValue = this.getControl(field.key)?.value;
    const selectedOption = field.options?.find(
      (option) => option.value === selectedValue,
    );

    return selectedOption?.label || field.placeholder || 'Select an option';
  }

  getFilteredOptions(field: FilterDrawerFieldConfig): FilterDrawerOption[] {
    const options = field.options ?? [];
    const searchTerm = (this.searchableTerms[field.key] ?? '').trim().toLowerCase();

    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => {
      const haystacks = [option.label, option.description ?? ''];
      return haystacks.some((value) => value.toLowerCase().includes(searchTerm));
    });
  }

  selectSearchableOption(
    fieldKey: string,
    option: FilterDrawerOption,
  ): void {
    if (option.disabled) {
      return;
    }

    this.getControl(fieldKey).setValue(option.value);
    this.searchableTerms[fieldKey] = '';
    this.openSearchableFieldKey = null;
  }

  private rebuildForm(useFieldDefaults = false): void {
    const nextControls: Record<string, FormControl<unknown>> = {};

    for (const field of this.fields) {
      const initialValue = useFieldDefaults
        ? this.getDefaultValueForField(field)
        : this.getFieldValue(field);

      const control = new FormControl(initialValue);

      if (field.disabled) {
        control.disable({ emitEvent: false });
      }

      nextControls[field.key] = control;

      if (field.type === 'searchable-select') {
        this.searchableTerms[field.key] = '';
      }
    }

    this.form = new FormGroup<Record<string, FormControl<unknown>>>(
      nextControls,
    );
  }

  private getFieldValue(field: FilterDrawerFieldConfig): unknown {
    if (Object.prototype.hasOwnProperty.call(this.initialValue, field.key)) {
      return (this.initialValue as Record<string, unknown>)[field.key];
    }

    return this.getDefaultValueForField(field);
  }

  private getDefaultValueForField(field: FilterDrawerFieldConfig): unknown {
    if (field.initialValue !== undefined) {
      return field.initialValue;
    }

    if (field.type === 'checkbox') {
      return false;
    }

    if (field.type === 'checkbox-group') {
      return [];
    }

    return '';
  }

  private getSanitizedValue(): FilterDrawerValue {
    const rawValue = this.form.getRawValue() as Record<string, unknown>;
    const sanitizedValue: FilterDrawerValue = {};

    for (const field of this.fields) {
      const currentValue = rawValue[field.key];

      if (typeof currentValue === 'string') {
        sanitizedValue[field.key] = currentValue.trim();
        continue;
      }

      sanitizedValue[field.key] = currentValue;
    }

    return sanitizedValue;
  }
}
